"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Link from "next/link";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";

const STORAGE_KEY = "hide_home_ads_popup";

type BannerData = {
  id: string;
  badgeEn: string | null;
  badgeZh: string | null;
  titleEn: string;
  titleZh: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  imageUrlEn: string | null;
  imageUrlZh: string | null;
  linkUrlEn: string | null;
  linkUrlZh: string | null;
};

export default function HomeAdsPopup() {
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [banner, setBanner] = useState<BannerData | null>(null);

  const locale = useLocale();
  const t = useTranslations("HomeAdsPopup");
  const isZh = locale === "zh";

  useEffect(() => {
    const hidden = localStorage.getItem(STORAGE_KEY);
    if (hidden === "true") return;

    fetch("/api/home-ads")
      .then((res) => res.json())
      .then((data) => {
        if (data.banner) {
          setBanner(data.banner);
          setTimeout(() => setOpen(true), 800);
        }
      })
      .catch((err) => console.error("[HomeAdsPopup] fetch error:", err));
  }, []);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, "true");
    }
    setOpen(false);
  };

  if (!banner) return null;

  const badge = (isZh ? banner.badgeZh : banner.badgeEn) || banner.badgeEn;
  const title = (isZh ? banner.titleZh : banner.titleEn) || banner.titleEn;
  const description =
    (isZh ? banner.descriptionZh : banner.descriptionEn) ||
    banner.descriptionEn;
  const imageUrl =
    (isZh ? banner.imageUrlZh : banner.imageUrlEn) || banner.imageUrlEn;
  const linkUrl =
    (isZh ? banner.linkUrlZh : banner.linkUrlEn) || banner.linkUrlEn;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 2,
          backgroundColor: "rgba(255,255,255,0.92)",
          "&:hover": {
            backgroundColor: "#fff",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 420, md: 460 },
            display: "flex",
            alignItems: "stretch",
            background:
              "linear-gradient(135deg, #f9f6ef 0%, #ffffff 45%, #f4efe3 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top left, rgba(212,175,55,0.18), transparent 30%), radial-gradient(circle at bottom right, rgba(139,94,60,0.12), transparent 28%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                p: { xs: 3, md: 5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {badge && (
                <Typography
                  sx={{
                    display: "inline-block",
                    mb: 1.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: "#8a6a2f",
                    backgroundColor: "rgba(212,175,55,0.12)",
                    width: "fit-content",
                  }}
                >
                  {badge}
                </Typography>
              )}

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.15,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  color: "#2b2b2b",
                }}
              >
                {title}
              </Typography>

              {description && (
                <Typography
                  sx={{
                    color: "#555",
                    mb: 3,
                    fontSize: { xs: 15, md: 16 },
                    maxWidth: 520,
                  }}
                >
                  {description}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                {linkUrl && (
                  <Button
                    component={Link}
                    href={linkUrl}
                    variant="contained"
                    sx={{
                      px: 3,
                      py: 1.2,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      background:
                        "linear-gradient(90deg, #b88a3b 0%, #d4af37 100%)",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                        background:
                          "linear-gradient(90deg, #a77d35 0%, #c7a42f 100%)",
                      },
                    }}
                  >
                    {t("learnMore")}
                  </Button>
                )}

                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={{
                    px: 3,
                    py: 1.2,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: "rgba(0,0,0,0.16)",
                    color: "#333",
                  }}
                >
                  {t("close")}
                </Button>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                    sx={{
                      color: "#9a9a9a",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 14, color: "#666" }}>
                    {t("dontShowAgain")}
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Box>

            {imageUrl && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: { xs: 220, md: "auto" },
                  position: "relative",
                  background:
                    "linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(255,255,255,0.2) 100%)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    minHeight: { xs: 220, md: 460 },
                  }}
                >
                  <Image
                    src={imageUrl}
                    alt={title}
                    fill
                    style={{ objectFit: "cover" }}
                    priority
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
